/**
 * Template Logo Selector
 * Allows users to select a logo from restaurant media library
 *
 * @file src/components/email/templates/TemplateLogoSelector.tsx
 * @created 2025-10-22
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ImageOff, X } from 'lucide-react';

interface TemplateLogoSelectorProps {
  organisationId: string;
  restaurantId?: string;
  selectedLogoUrl: string | null;
  onLogoSelect: (url: string | null) => void;
}

interface LogoImage {
  id: string;
  public_url: string;
  original_filename: string | null;
  tags?: string[];
}

/**
 * TemplateLogoSelector Component
 *
 * Fetches logos from restaurant_media table (filtered by 'logo' tag)
 * and displays them in a grid for selection.
 *
 * @example
 * <TemplateLogoSelector
 *   organisationId="org-123"
 *   restaurantId="rest-456"
 *   selectedLogoUrl={headerLogo}
 *   onLogoSelect={setHeaderLogo}
 * />
 */
const TemplateLogoSelector: React.FC<TemplateLogoSelectorProps> = ({
  organisationId,
  restaurantId,
  selectedLogoUrl,
  onLogoSelect
}) => {
  const [logos, setLogos] = useState<LogoImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLogos();
  }, [organisationId, restaurantId]);

  const fetchLogos = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[TemplateLogoSelector] Fetching logos for organisation:', organisationId);

      // Query restaurant_media for images tagged as 'logo' or 'Logo' (case-insensitive)
      let query = supabase
        .from('restaurant_media')
        .select('id, public_url, original_filename, tags')
        .eq('organisation_id', organisationId)
        .eq('media_type', 'image')
        .eq('status', 'completed')
        .order('created_at', { ascending: false });

      // Filter by restaurant if provided
      if (restaurantId) {
        query = query.eq('restaurant_id', restaurantId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      // Filter for logos case-insensitively
      const logoImages = (data || []).filter(img =>
        img.tags?.some((tag: string) => tag.toLowerCase() === 'logo')
      );

      console.log(`[TemplateLogoSelector] Found ${logoImages.length} logos`);
      setLogos(logoImages);

    } catch (err: any) {
      console.error('[TemplateLogoSelector] Error fetching logos:', err);
      setError(err.message || 'Failed to load logos');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoClick = (url: string) => {
    if (selectedLogoUrl === url) {
      // Clicking selected logo deselects it
      onLogoSelect(null);
    } else {
      onLogoSelect(url);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-600">Loading logos...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (logos.length === 0) {
    return (
      <div className="text-center py-8">
        <ImageOff className="h-12 w-12 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600 mb-1">No logos found</p>
        <p className="text-xs text-gray-500">
          Upload logos to your media library with the 'logo' tag
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected Logo Preview */}
      {selectedLogoUrl && (
        <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-700">Selected Logo</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onLogoSelect(null)}
              className="h-6 px-2 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Remove
            </Button>
          </div>
          <div className="flex items-center justify-center bg-white rounded-md p-4 border border-gray-200">
            <img
              src={selectedLogoUrl}
              alt="Selected logo"
              className="max-h-16 max-w-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Logo Grid */}
      <div>
        <p className="text-xs font-medium text-gray-700 mb-2">
          Available Logos ({logos.length})
        </p>
        <div className="grid grid-cols-3 gap-3">
          {logos.map((logo) => (
            <button
              key={logo.id}
              onClick={() => handleLogoClick(logo.public_url)}
              className={`
                relative p-3 bg-white rounded-md border-2 transition-all
                hover:shadow-md hover:scale-105
                ${selectedLogoUrl === logo.public_url
                  ? 'border-primary ring-2 ring-primary ring-offset-2'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
              title={logo.original_filename || 'Logo'}
            >
              <div className="aspect-square flex items-center justify-center">
                <img
                  src={logo.public_url}
                  alt={logo.original_filename || 'Logo'}
                  className="max-h-full max-w-full object-contain"
                  loading="lazy"
                />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* No Logo Option */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onLogoSelect(null)}
        className="w-full"
        disabled={selectedLogoUrl === null}
      >
        <ImageOff className="h-4 w-4 mr-2" />
        Use No Logo
      </Button>

      <p className="text-xs text-gray-500 text-center">
        Select a logo or choose "Use No Logo" to use a placeholder
      </p>
    </div>
  );
};

export default TemplateLogoSelector;
